import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { billService } from '../../services/bills/billService'
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler'

export const useBills = (params?: {
  page?: number
  limit?: number
  customer_name?: string
  customer_id?: string
  status?: string
  payment_status?: string
  booker_id?: string
  area?: string
  date?: string
}) => {
  return useQuery({
    queryKey: ['bills', params],
    queryFn: () => billService.getBills(params),
  })
}

export const useBill = (id: string) => {
  return useQuery({
    queryKey: ['bill', id],
    queryFn: () => billService.getBillById(id),
    enabled: !!id,
  })
}


export const useCreateBill = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      customer_id: string
      sku_items: { sku_id: string; quantity_ctn: number; loose_pcs?: number }[]
      remarks?: string
    }) => billService.createBill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      showSuccessToast('Bill created successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useUpdateBill = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      billService.updateBill(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      queryClient.invalidateQueries({ queryKey: ['bill', id] })
      showSuccessToast('Bill updated successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useDeleteBill = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: string) => billService.deleteBill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] })
      showSuccessToast('Bill deleted successfully!')
    },
    onError: (error: any) => {
      // Check if it's the specific load form error
      if (error?.message?.includes('Cannot delete bill from load form')) {
        showErrorToast({ message: 'Cannot delete bill from load form' })
      } else {
        showErrorToast(error)
      }
    },
  })
}

export const useViewBillInvoice = () => {
  return useMutation({
    mutationFn: (id: string) => billService.printBillHtml(id),
    onSuccess: (htmlContent: string) => {
      const viewWindow = window.open('', '_blank')
      if (viewWindow) {
        viewWindow.document.write(htmlContent)
        viewWindow.document.close()
        viewWindow.focus()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const usePrintBill = () => {
  return useMutation({
    mutationFn: (id: string) => billService.printBillHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content and print it
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        printWindow.focus()
        printWindow.print()
        printWindow.close()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

